import React, { useState } from 'react';
import { FinancialValue } from '../shared/FinancialValue';

export interface CarrierCounterModelPanelProps {
  carrier: {
    available: boolean;
    attack_vector: string | null;
    attack_strength: number | null;
    daubert_score: number | null;
    alternative_figure: number | null;
    depo_questions: string[];
  };
  electedGross: number | null;
}

function getBandWord(score: number | null): string {
  if (score == null) return 'UNKNOWN';
  if (score <= 33) return 'LOW';
  if (score <= 66) return 'MODERATE';
  return 'STRONG';
}

function formatDelta(value: number | null, electedGross: number | null): string {
  if (value == null || electedGross == null || electedGross === 0) return '—';
  const delta = value - electedGross;
  const pct = (delta / electedGross) * 100;
  const absDelta = Math.abs(delta);
  const formatted = absDelta.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const sign = delta < 0 ? '' : '+';
  return `(${sign}$${formatted}) ${sign}${pct.toFixed(1)}% vs elected gross`;
}

export const CarrierCounterModelPanel: React.FC<CarrierCounterModelPanelProps> = ({
  carrier,
  electedGross,
}) => {
  const [showDepo, setShowDepo] = useState(false);

  const FRAMING =
    "The other side's counter-position, surfaced before they raise it. This informs your election — it does not gate it.";

  if (!carrier.available) {
    return (
      <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-6 mt-6">
        <div className="flex justify-between items-center mb-4">
          <span className="text-[10px] uppercase tracking-[0.08em] text-[var(--color-text-muted)]">
            CARRIER COUNTER-MODEL · CASE-LEVEL
          </span>
          <span className="text-[11px] text-[var(--color-text-muted)]">Source: fleet defense run</span>
        </div>
        <p className="text-[12px] text-[var(--color-text-secondary)] mb-6">{FRAMING}</p>
        <p className="text-[12px] text-[var(--color-text-muted)]">
          No carrier counter-model on file. This panel populates when the case's defense run completes.
        </p>
      </div>
    );
  }

  const attackStrengthBand = getBandWord(carrier.attack_strength);
  const daubertBand = getBandWord(carrier.daubert_score);

  return (
    <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-6 mt-6">
      <div className="flex justify-between items-center mb-4">
        <span className="text-[10px] uppercase tracking-[0.08em] text-[var(--color-text-muted)]">
          CARRIER COUNTER-MODEL · CASE-LEVEL
        </span>
        <span className="text-[11px] text-[var(--color-text-muted)]">Source: fleet defense run</span>
      </div>

      <p className="text-[12px] text-[var(--color-text-secondary)] mb-6">{FRAMING}</p>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7">
          <div className="text-[10px] uppercase tracking-[0.08em] text-[var(--color-text-muted)] mb-2">
            PRIMARY ATTACK VECTOR
          </div>
          <p className="text-[14px] text-[var(--color-text-primary)] leading-relaxed opacity-85">
            {carrier.attack_vector || 'No attack vector specified.'}
          </p>
        </div>

        <div className="lg:col-span-5 space-y-4">
          <div className="pb-4 border-b border-[var(--color-border)]">
            <div className="flex justify-between items-baseline mb-1">
              <div>
                <div className="text-[11px] text-[var(--color-text-primary)]">Attack strength</div>
                <div className="text-[10px] text-[var(--color-text-muted)]">
                  Strength of the carrier's overall counter-position
                </div>
              </div>
              <div className="text-right">
                <div className="font-mono text-[20px] font-semibold text-[var(--color-text-primary)]">
                  {carrier.attack_strength ?? '—'}
                  <span className="text-[var(--color-text-muted)] text-[14px] font-medium">/100</span>
                </div>
                <div className="text-[11px] font-semibold uppercase text-[var(--color-text-secondary)] text-right">
                  {attackStrengthBand}
                </div>
              </div>
            </div>
            <div className="h-1 bg-[var(--color-border)] rounded-full mt-2">
              <div
                className="h-1 bg-[var(--color-text-muted)] rounded-full"
                style={{ width: `${carrier.attack_strength ?? 0}%` }}
              />
            </div>
          </div>

          <div className="pb-4 border-b border-[var(--color-border)]">
            <div className="flex justify-between items-baseline mb-1">
              <div>
                <div className="text-[11px] text-[var(--color-text-primary)]">Daubert exposure</div>
                <div className="text-[10px] text-[var(--color-text-muted)]">
                  Carrier's odds of excluding the elected methodology on Daubert
                </div>
              </div>
              <div className="text-right">
                <div className="font-mono text-[20px] font-semibold text-[var(--color-text-primary)]">
                  {carrier.daubert_score ?? '—'}
                  <span className="text-[var(--color-text-muted)] text-[14px] font-medium">/100</span>
                </div>
                <div className="text-[11px] font-semibold uppercase text-[var(--color-text-secondary)] text-right">
                  {daubertBand}
                </div>
              </div>
            </div>
            <div className="h-1 bg-[var(--color-border)] rounded-full mt-2">
              <div
                className="h-1 bg-[var(--color-text-muted)] rounded-full"
                style={{ width: `${carrier.daubert_score ?? 0}%` }}
              />
            </div>
          </div>

          <div>
            <div className="flex justify-between items-baseline mb-1">
              <span className="text-[11px] text-[var(--color-text-primary)]">Carrier's alternative figure</span>
              <FinancialValue value={carrier.alternative_figure} size="md" />
            </div>
            <div className="text-right font-mono text-[11px] text-[var(--color-blocker)]">
              {formatDelta(carrier.alternative_figure, electedGross)}
            </div>
          </div>
        </div>
      </div>

      <div className="pt-4 mt-6 border-t border-[var(--color-border)]">
        <button
          onClick={() => setShowDepo(!showDepo)}
          aria-expanded={showDepo}
          className="flex items-center gap-2 text-[11px] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] transition-colors duration-150"
        >
          <span>{showDepo ? '▾' : '▸'}</span>
          <span>Deposition questions to prepare ({carrier.depo_questions.length})</span>
        </button>

        {showDepo && (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
            {carrier.depo_questions.map((question, idx) => (
              <div key={idx} className="flex gap-3">
                <span className="text-[12px] font-mono text-[var(--color-text-muted)] w-6 shrink-0">
                  {String(idx + 1).padStart(2, '0')}
                </span>
                <span className="text-[13px] text-[var(--color-text-secondary)]">{question}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CarrierCounterModelPanel;
